import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { usersApi } from '../api/users';
import { s3Api } from '../api/s3';
import defaultUserPhoto from '../assets/Assemblie_DefaultUserIcon.png';
import ImageUploadModal from '../components/ImageUploadModal';
import '../pages/DashboardPages.css';

function DashboardAdminUserManagement() {
	const { orgData } = useAuth();
	const [users, setUsers] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isImageModalOpen, setIsImageModalOpen] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState(null);
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		email: '',
		password: '',
		phoneNumber: '',
		userPhoto: '',
		accessLevel: 'User',
		visibilityStatus: 'private',
		orgPin: '',
	});

	useEffect(() => {
		fetchUsers();
	}, []);

	const fetchUsers = async () => {
		try {
			const response = await usersApi.getAllAdmin(); // New admin endpoint needed
			setUsers(response.users);
		} catch (error) {
			setError('Failed to fetch users');
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleImageSave = async ({ file }) => {
		try {
			setError('');
			const avatarUrl = await s3Api.uploadAvatar(
				orgData.id,
				selectedUserId,
				file
			);

			// Update the user's avatar in the database
			await usersApi.update(selectedUserId, { userPhoto: avatarUrl });

			// Update the local state
			setUsers(
				users.map((user) =>
					user.id === selectedUserId
						? { ...user, userPhoto: avatarUrl }
						: user
				)
			);

			setIsImageModalOpen(false);
		} catch (error) {
			setError(`Failed to upload avatar: ${error.message}`);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			// First create the user to get their ID
			const newUser = await usersApi.createAdmin(formData);

			// If there's a photo file, upload it
			if (formData.userPhotoFile) {
				const avatarUrl = await s3Api.uploadAvatar(
					orgData.id,
					newUser.id,
					formData.userPhotoFile
				);

				// Update the user with the avatar URL
				await usersApi.update(newUser.id, { userPhoto: avatarUrl });
			}

			fetchUsers();
			closeModal();
		} catch (err) {
			setError(err.message || 'Failed to create user');
		}
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setFormData({
			firstName: '',
			lastName: '',
			email: '',
			password: '',
			phoneNumber: '',
			userPhoto: '',
			accessLevel: 'User',
			visibilityStatus: 'private',
			orgPin: '',
		});
	};

	const filteredUsers = users.filter(
		(user) =>
			user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.email.toLowerCase().includes(searchTerm.toLowerCase())
	);

	return (
		<DashboardLayout>
			<div className='dashboard-page'>
				<h1>User Management</h1>
				<div className='content-section'>
					<div className='admin-controls'>
						<input
							type='text'
							placeholder='Search users...'
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className='search-input'
						/>
						<button
							className='create-btn'
							onClick={() => setIsModalOpen(true)}>
							Create New User
						</button>
					</div>

					{error && <div className='error-message'>{error}</div>}

					{isLoading ? (
						<p>Loading users...</p>
					) : (
						<div className='table-responsive'>
							<table className='users-table'>
								<thead>
									<tr>
										<th>Photo</th>
										<th>Name</th>
										<th>Email</th>
										<th>Phone</th>
										<th>Access Level</th>
										<th>Status</th>
									</tr>
								</thead>
								<tbody>
									{filteredUsers.map((user) => (
										<tr key={user.id}>
											<td>
												<div
													className='avatar-container'
													onClick={() => {
														setSelectedUserId(
															user.id
														);
														setIsImageModalOpen(
															true
														);
													}}>
													<img
														src={
															user.userPhoto ||
															defaultUserPhoto
														}
														alt={`${user.firstName} ${user.lastName}`}
														className='user-avatar'
													/>
													<div className='avatar-overlay'>
														<span className='material-icons'>
															photo_camera
														</span>
													</div>
												</div>
											</td>
											<td>{`${user.firstName} ${user.lastName}`}</td>
											<td>{user.email}</td>
											<td>{user.phoneNumber}</td>
											<td>{user.accessLevel}</td>
											<td>{user.visibilityStatus}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}

					{isModalOpen && (
						<div className='announcement-modal-overlay'>
							<div className='announcement-modal'>
								<div className='modal-header'>
									<h2 className='modal-title'>
										Create New User
									</h2>
								</div>
								<div className='modal-content-scroll'>
									<form
										onSubmit={handleSubmit}
										className='modal-form'>
										<div className='form-group'>
											<label>First Name</label>
											<input
												type='text'
												value={formData.firstName}
												onChange={(e) =>
													setFormData({
														...formData,
														firstName:
															e.target.value,
													})
												}
												required
												className='form-input'
											/>
										</div>

										<div className='form-group'>
											<label>Last Name</label>
											<input
												type='text'
												value={formData.lastName}
												onChange={(e) =>
													setFormData({
														...formData,
														lastName:
															e.target.value,
													})
												}
												required
												className='form-input'
											/>
										</div>

										<div className='form-group'>
											<label>Email</label>
											<input
												type='email'
												value={formData.email}
												onChange={(e) =>
													setFormData({
														...formData,
														email: e.target.value,
													})
												}
												required
												className='form-input'
											/>
										</div>

										<div className='form-group'>
											<label>Password</label>
											<input
												type='password'
												value={formData.password}
												onChange={(e) =>
													setFormData({
														...formData,
														password:
															e.target.value,
													})
												}
												required
												className='form-input'
											/>
										</div>

										<div className='form-group'>
											<label>Phone Number</label>
											<input
												type='tel'
												value={formData.phoneNumber}
												onChange={(e) =>
													setFormData({
														...formData,
														phoneNumber:
															e.target.value,
													})
												}
												required
												className='form-input'
											/>
										</div>

										<div className='form-group'>
											<label>
												Organization PIN (Optional)
											</label>
											<input
												type='text'
												value={formData.orgPin}
												onChange={(e) =>
													setFormData({
														...formData,
														orgPin: e.target.value,
													})
												}
												className='form-input'
												placeholder='Enter organization PIN'
											/>
										</div>

										<div className='form-group'>
											<label>User Photo</label>
											<input
												type='file'
												accept='image/*'
												onChange={(e) => {
													const file =
														e.target.files[0];
													if (file) {
														setFormData({
															...formData,
															userPhotoFile: file, // Store the file temporarily
														});
													}
												}}
												className='form-input'
											/>
										</div>

										<div className='form-group'>
											<label>Access Level</label>
											<select
												value={formData.accessLevel}
												onChange={(e) =>
													setFormData({
														...formData,
														accessLevel:
															e.target.value,
													})
												}
												className='form-input'>
												<option value='User'>
													User
												</option>
												<option value='Admin'>
													Admin
												</option>
												<option value='SuperAdmin'>
													Super Admin
												</option>
											</select>
										</div>

										<div className='form-group'>
											<label>Visibility Status</label>
											<select
												value={
													formData.visibilityStatus
												}
												onChange={(e) =>
													setFormData({
														...formData,
														visibilityStatus:
															e.target.value,
													})
												}
												className='form-input'>
												<option value='private'>
													Private
												</option>
												<option value='public'>
													Public
												</option>
											</select>
										</div>

										<div className='modal-actions'>
											<button
												type='button'
												onClick={closeModal}
												className='modal-button modal-button-secondary'>
												Cancel
											</button>
											<button
												type='submit'
												className='modal-button modal-button-primary'>
												Create User
											</button>
										</div>
									</form>
								</div>
							</div>
						</div>
					)}

					{isImageModalOpen && (
						<ImageUploadModal
							isOpen={isImageModalOpen}
							onClose={() => {
								setIsImageModalOpen(false);
								setSelectedUserId(null);
							}}
							onSave={handleImageSave}
							currentImage={
								users.find((u) => u.id === selectedUserId)
									?.userPhoto
							}
							shouldCrop={true}
						/>
					)}
				</div>
			</div>
		</DashboardLayout>
	);
}

export default DashboardAdminUserManagement;
